import {Box, Checkbox, FormControlLabel, makeStyles, Typography} from "@material-ui/core";
import React, {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {MultiSelectBox, Option, PeriodPicker} from "components";

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  row: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  groupLabel: {
    fontSize: "14px",
    color: "#353945",
    fontWeight: 500,
    width: "140px",
    flexShrink: 0,
  },
  filter: {
    width: "160px",
    "@media (max-width: 800px)": {
      width: "100% !important",
    },
    "& .MuiOutlinedInput-root": {
      height: "44px !important",
    },
    "& .MuiInputAdornment-filled.MuiInputAdornment-positionStart:not(.MuiInputAdornment-hiddenLabel)": {
      marginTop: "0px",
    },
    "& .MuiInputLabel-filled ": {
      transform: " translate(12px, 25px) scale(1)",
    },
  },
}));

interface DateInterval {
  startDate: number;
  endDate: number;
}

export interface AnalysisFiltersModel {
  uploadPeriodStart?: DateInterval;
  uploadPeriodEnd?: DateInterval;
  contentPeriodStart?: DateInterval;
  contentPeriodEnd?: DateInterval;
  importerIds: string[];
  operatorIds: string[];
  carrierIds: string[];
  territoryCodes: string[];
  successOnly: boolean;
}

interface AnalysisFiltersProps {
  importers: Option[];
  operators: Option[];
  carriers: Option[];
  territories: Option[];
  filterValues: AnalysisFiltersModel;
  onFilterChange: (values: AnalysisFiltersModel) => void;
}

const sortOptions = (options: Option[]): Option[] => {
  return [...options].sort((a, b) => a.label.localeCompare(b.label, "fr"));
};

export const AnalysisFilters = (props: AnalysisFiltersProps) => {
  const { importers, operators, carriers, territories, filterValues, onFilterChange } = props;
  const classes = useStyles();
  const { t } = useTranslation();

  const sortedImporters = useMemo(() => sortOptions(importers), [importers]);
  const sortedOperators = useMemo(() => sortOptions(operators), [operators]);
  const sortedCarriers = useMemo(() => sortOptions(carriers), [carriers]);
  const sortedTerritories = useMemo(() => sortOptions(territories), [territories]);

  const [uploadStart, setUploadStart] = useState<DateInterval | undefined>(filterValues.uploadPeriodStart);
  const [uploadEnd, setUploadEnd] = useState<DateInterval | undefined>(filterValues.uploadPeriodEnd);
  const [contentStart, setContentStart] = useState<DateInterval | undefined>(filterValues.contentPeriodStart);
  const [contentEnd, setContentEnd] = useState<DateInterval | undefined>(filterValues.contentPeriodEnd);

  const handlePeriodChange = useCallback(
    (field: keyof AnalysisFiltersModel, setter: (v: DateInterval | undefined) => void) =>
      (period: DateInterval | undefined) => {
        setter(period);
        onFilterChange({ ...filterValues, [field]: period });
      },
    [onFilterChange, filterValues]
  );

  const handleFilterChange = (property: keyof AnalysisFiltersModel, value: any) => {
    onFilterChange({ ...filterValues, [property]: value });
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.row}>
        <Typography className={classes.groupLabel}>{t("received_data_analysis_filter_upload")}</Typography>
        <PeriodPicker
          id="filterUploadStart"
          placeholder={t("received_data_analysis_period_start")}
          period={uploadStart}
          onDateChange={handlePeriodChange("uploadPeriodStart", setUploadStart)}
          inputClassName={classes.filter}
          clearable
        />
        <PeriodPicker
          id="filterUploadEnd"
          placeholder={t("received_data_analysis_period_end")}
          period={uploadEnd}
          onDateChange={handlePeriodChange("uploadPeriodEnd", setUploadEnd)}
          inputClassName={classes.filter}
          clearable
        />
      </Box>
      <Box className={classes.row}>
        <Typography className={classes.groupLabel}>{t("received_data_analysis_filter_content")}</Typography>
        <PeriodPicker
          id="filterContentStart"
          placeholder={t("received_data_analysis_period_start")}
          period={contentStart}
          onDateChange={handlePeriodChange("contentPeriodStart", setContentStart)}
          inputClassName={classes.filter}
          clearable
        />
        <PeriodPicker
          id="filterContentEnd"
          placeholder={t("received_data_analysis_period_end")}
          period={contentEnd}
          onDateChange={handlePeriodChange("contentPeriodEnd", setContentEnd)}
          inputClassName={classes.filter}
          clearable
        />
      </Box>
      <Box className={classes.row}>
        <Typography className={classes.groupLabel}>{t("received_data_analysis_filter_orgs")}</Typography>
        <MultiSelectBox
          id="analysis-filters-importers"
          label={""}
          options={sortedImporters}
          onChange={(ids) => handleFilterChange("importerIds", ids ?? [])}
          values={filterValues.importerIds}
          placeHolder={t("received_data_analysis_importer")}
          className={`${classes.filter} project-filters`}
        />
        <MultiSelectBox
          id="analysis-filters-operators"
          label={""}
          options={sortedOperators}
          onChange={(ids) => handleFilterChange("operatorIds", ids ?? [])}
          values={filterValues.operatorIds}
          placeHolder={t("received_data_analysis_operator")}
          className={`${classes.filter} project-filters`}
        />
        <MultiSelectBox
          id="analysis-filters-carriers"
          label={""}
          options={sortedCarriers}
          onChange={(ids) => handleFilterChange("carrierIds", ids ?? [])}
          values={filterValues.carrierIds}
          placeHolder={t("received_data_analysis_carrier")}
          className={`${classes.filter} project-filters`}
        />
        <MultiSelectBox
          id="analysis-filters-territories"
          label={""}
          options={sortedTerritories}
          onChange={(ids) => handleFilterChange("territoryCodes", ids ?? [])}
          values={filterValues.territoryCodes}
          placeHolder={t("received_data_analysis_territory")}
          className={`${classes.filter} project-filters`}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={filterValues.successOnly}
              onChange={(e) => handleFilterChange("successOnly", e.target.checked)}
              color="primary"
              size="small"
            />
          }
          label={t("received_data_analysis_show_success_only")}
        />
      </Box>
    </Box>
  );
};
